/**
 * Where in-app ads (app-ad-banner + app-native-ad) are allowed to render.
 *
 * Two separate decisions live here:
 *   ADS_ENABLED — the kill switch. Flip to `false` to pull every ad placement
 *                 app-wide without touching a template. Both ad components read
 *                 it and collapse their host element, so no empty gap is left.
 *   AD_HOSTS    — which deployments actually serve ads.
 *
 * AdResolverService is the single consumer of `adsAllowedForHost` — every ad
 * placement is guarded by it.
 */
export const ADS_ENABLED = true;

/**
 * Hosts that serve ads. Matched EXACTLY, so each variant must be listed:
 * `www.homecinemaplus.tv` is a different hostname than `homecinemaplus.tv`, and
 * omitting it silently drops every impression from www traffic.
 *
 * This is the HomeCinema white-label build, so it lists the HomeCinema domains —
 * NOT toqqer.com, which serves its own build off gke-dev with its own ad keys
 * (see ad-config.ts). Dev and staging hosts are deliberately absent.
 */
export const AD_HOSTS: readonly string[] = ['homecinemaplus.tv', 'www.homecinemaplus.tv'];

/**
 * localStorage escape hatch so ads can be exercised on localhost during
 * development: `localStorage.setItem('adsDebug', '1')`. Never true on a real
 * deployment unless someone sets it in their own browser, and it still cannot
 * override ADS_ENABLED.
 */
export const AD_DEBUG_KEY = 'adsDebug';

/**
 * Outlines every ad placement so it can be spotted on a test box even when the
 * ad network serves nothing (an unfilled slot is an invisible iframe, which is
 * indistinguishable from a slot that never rendered at all).
 *
 * Red   = banner (app-ad-banner)
 * Green = native (app-native-ad) — should NEVER appear on desktop.
 *
 * Must stay `false` on any deployed build.
 */
export const AD_DEBUG_OUTLINE = false;

/**
 * Local dev hosts. Ads render here so the placements can be seen while working.
 * The network still won't fill an unrecognised origin, but the layout and the
 * wiring are exercised. NOT keyed off `environment.production` — the two
 * environment files in this repo have that flag inverted.
 */
const DEV_HOSTS: readonly string[] = ['localhost', '127.0.0.1', '0.0.0.0', ''];

/** Pure so it can be unit-tested without stubbing `window.location`. */
export function adsAllowedForHost(hostname: string, debugOverride = false): boolean {
  if (!ADS_ENABLED) { return false; }
  return AD_HOSTS.includes(hostname) || DEV_HOSTS.includes(hostname) || debugOverride;
}

/** A dev/staging box, where placements render without a real ad-serving domain. */
export function isDevHost(hostname: string): boolean {
  return DEV_HOSTS.includes(hostname);
}
